import React, { useState } from "react";
import { Mail, ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";
import { useFormValidation } from "../../../../hooks";
import { authApi } from "../../../../api/authApi";
import B2BSuccessMessage from "../../B2BShared/B2BSuccessMessage/B2BSuccessMessage";
import B2BErrorMessage from "../../B2BShared/B2BErrorMessage/B2BErrorMessage";
import { useB2BTranslation } from "../../B2BShared/hooks/useB2BTranslation";
import "./B2BForgotPassword.css";

interface FormErrors {
  general?: string;
  email?: string;
  [key: string]: string | undefined;
}

const B2BForgotPassword: React.FC = () => {
  const { tAuth, tCommon } = useB2BTranslation();
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);

  const {
    formData,
    errors,
    isLoading,
    setIsLoading,
    handleInputChange,
    setGeneralError,
    clearErrors,
  } = useFormValidation({ email: "" });

  const typedErrors = errors as FormErrors;

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();

    if (!formData.email.trim()) {
      setGeneralError(tAuth("emailRequired"));
      return;
    }

    if (!/\S+@\S+\.\S+/.test(formData.email)) {
      setGeneralError(tAuth("emailInvalid"));
      return;
    }

    setIsLoading(true);
    clearErrors();

    try {
      await authApi.forgotPassword(formData.email);
      setIsSubmitted(true);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : tAuth("forgotPasswordError");
      setGeneralError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="b2b-forgot-password-container">
        <div className="b2b-forgot-password-card">
          <B2BSuccessMessage
            title={tAuth("forgotPasswordSuccessTitle")}
            message={tAuth("forgotPasswordSuccessMessage")}
            additionalInfo={formData.email}
          />

          <div className="b2b-forgot-password-actions">
            <Link
              to="/gcb/login"
              className="b2b-back-to-login-btn"
            >
              {tAuth("backToLogin")}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="b2b-forgot-password-container">
      <div className="b2b-forgot-password-card">
        <div className="back-to-b2b-container">
          <Link
            to="/gcb"
            className="b2b-auth-back-button"
          >
            <ArrowLeft />
            <span>{tAuth("backToB2BPage") || "Back to main GCB Page"}</span>
          </Link>
        </div>

        <div className="b2b-forgot-password-header">
          <h1 className="b2b-forgot-password-title">
            {tAuth("forgotPassword")}
          </h1>
          <p className="b2b-forgot-password-subtitle">
            {tAuth("forgotPasswordDescription")}
          </p>
        </div>

        <form
          onSubmit={handleSubmit}
          className="b2b-forgot-password-form"
        >
          <div className="b2b-form-group">
            <div className="b2b-input-container">
              <Mail className="b2b-input-icon" />
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder={tAuth("email")}
                className={`b2b-input ${typedErrors.general || typedErrors.email ? "b2b-input-error" : ""}`}
                disabled={isLoading}
              />
            </div>
            <B2BErrorMessage
              error={typedErrors.general || typedErrors.email}
              variant="inline"
            />
          </div>

          <button
            type="submit"
            className="b2b-forgot-password-button"
            disabled={isLoading}
          >
            {isLoading ? tCommon("loading") : tAuth("sendResetLink")}
          </button>

          <div className="b2b-forgot-password-footer">
            <span>{tAuth("rememberPassword")} </span>
            <Link
              to="/gcb/login"
              className="b2b-login-link"
            >
              {tAuth("signIn")}
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
};

export default B2BForgotPassword;

